import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ArrowRight, Search, Lock, UserPlus, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const IMPORT_API = `${API_BASE}/api/subscription-management/import`;
const errorText = (e, fallback) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : fallback; };

export async function matchCustomer(excelName, customer) {
  const token = localStorage.getItem('token');
  await axios.post(`${IMPORT_API}/match-customers?token=${token}`, { matches: [{ excel_name: excelName, customer_id: customer.customer_id }] });
  return { type: 'matched', name: customer.name };
}

export async function createCustomer(item) {
  const token = localStorage.getItem('token');
  const res = await axios.post(`${IMPORT_API}/create-customers?token=${token}`, {
    customers: [{ excel_name: item.excel_name, phone: item.phone || null, device_count: item.row_count || 0 }]
  });
  if (res.data.skipped?.length) return { type: 'matched', name: res.data.skipped[0].name };
  return { type: 'created', name: item.excel_name };
}

export const useCustomers = () => {
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/api/customers?token=${token}`).then(r => setCustomers(r.data)).catch(() => {});
  }, []);
  return customers;
};

function CustomerSearch({ customers, onPick }) {
  const [q, setQ] = useState('');
  const hits = q.length >= 2 ? customers.filter(c => c.name?.toLocaleLowerCase('tr').includes(q.toLocaleLowerCase('tr'))).slice(0, 6) : [];
  return (
    <div className="mt-2">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <Input className="h-8 pl-7 text-xs" data-testid="crm-customer-search-input" placeholder="CRM'de başka müşteri ara" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {hits.length > 0 && (
        <div className="border rounded mt-1 divide-y max-h-40 overflow-y-auto bg-white">
          {hits.map(c => (
            <button key={c.id} type="button" className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50"
              onClick={() => { onPick({ customer_id: c.id, name: c.name, score: null }); setQ(''); }}>{c.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export function LockedBadge({ lock }) {
  return (
    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1.5" data-testid="locked-badge">
      <Lock className="w-3.5 h-3.5 shrink-0" />
      {lock.type === 'created' ? 'Yeni müşteri olarak CRM\'e eklendi' : <>Eşleştirildi: <b className="truncate">{lock.name}</b></>}
    </div>
  );
}

function ConfirmDialog({ action, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!action) return null;
  const isCreate = action.type === 'create';
  const run = async () => {
    setBusy(true);
    try {
      const lock = isCreate ? await createCustomer(action.item) : await matchCustomer(action.item.excel_name, action.customer);
      toast.success(isCreate ? (lock.type === 'created' ? 'Yeni müşteri eklendi' : 'Bu ünvan CRM\'de zaten vardı, eşleştirildi') : 'Müşteri ünvanı güncellendi');
      onDone(action.item.excel_name, lock);
      onClose();
    } catch (e) {
      toast.error(errorText(e, 'İşlem yapılamadı'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="unmatched-confirm-dialog">
        <DialogHeader><DialogTitle>{isCreate ? 'Yeni müşteri olarak eklensin mi?' : 'Müşteri ünvanı güncellensin mi?'}</DialogTitle></DialogHeader>
        {isCreate ? (
          <div className="text-sm space-y-1">
            <p className="font-medium text-gray-800">{action.item.excel_name}</p>
            {action.item.phone && <p className="text-gray-500">Telefon: {action.item.phone}</p>}
            <p className="text-gray-500">CRM'e yeni müşteri kaydı olarak eklenecek.</p>
          </div>
        ) : (
          <>
            <div className="border rounded p-3 text-sm grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
              <span className="text-gray-500 line-through">{action.customer.name}</span>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <span className="font-medium text-gray-800">{action.item.excel_name}</span>
            </div>
            <p className="text-xs text-gray-500">CRM'deki ünvan Excel'deki ünvanla güncellenir. Eski ünvan müşteri kaydında saklanır.</p>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button data-testid="unmatched-confirm-submit" onClick={run} disabled={busy}>{busy ? 'İşleniyor...' : 'Onayla'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnmatchedRow({ item, lock, customers, onAsk }) {
  const [selected, setSelected] = useState(null);
  const options = [...item.suggestions];
  if (selected && !options.some(o => o.customer_id === selected.customer_id)) options.push(selected);
  return (
    <div className="py-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 md:items-start" data-testid={`unmatched-row-${item.excel_name}`}>
      <div>
        <p className="text-xs text-gray-400">Excel ünvanı ({item.row_count} cihaz)</p>
        <p className="text-sm font-medium text-gray-800">{item.excel_name}</p>
      </div>
      <ArrowRight className="hidden md:block w-4 h-4 text-gray-300 mt-5" />
      <div>
        <p className="text-xs text-gray-400">CRM'deki müşteri</p>
        {lock ? <div className="mt-1"><LockedBadge lock={lock} /></div> : (
          <>
            {options.length === 0 && <p className="text-xs text-gray-500">Benzer müşteri bulunamadı</p>}
            <div className="flex flex-col gap-1 mt-1">
              {options.map(o => {
                const active = selected?.customer_id === o.customer_id;
                return (
                  <button key={o.customer_id} type="button" data-testid={`unmatched-option-${o.customer_id}`}
                    onClick={() => setSelected(active ? null : o)}
                    className={`text-left text-sm px-2 py-1.5 rounded border transition-colors ${active ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}>
                    {o.name}{o.score != null && <span className="ml-2 text-xs text-gray-400">%{o.score} benzer</span>}
                  </button>
                );
              })}
            </div>
            <CustomerSearch customers={customers} onPick={setSelected} />
            <div className="flex flex-wrap gap-2 mt-2">
              <Button size="sm" className="h-7 text-xs flex items-center gap-1" disabled={!selected} data-testid="unmatched-row-confirm-btn"
                onClick={() => onAsk({ type: 'match', item, customer: selected })}><Check className="w-3.5 h-3.5" />Eşleştir</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs flex items-center gap-1" data-testid="unmatched-row-create-btn"
                onClick={() => onAsk({ type: 'create', item })}><UserPlus className="w-3.5 h-3.5" />Yeni Müşteri Olarak Ekle</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function MatchCustomerDialog({ item, customers, onClose, onDone }) {
  const [picked, setPicked] = useState(null);
  const [ask, setAsk] = useState(null);
  useEffect(() => { setPicked(null); }, [item]);
  if (!item) return null;
  if (ask) return <ConfirmDialog action={ask} onClose={() => { setAsk(null); onClose(); }} onDone={onDone} />;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="match-customer-dialog">
        <DialogHeader><DialogTitle>CRM'de müşteri ara ve eşleştir</DialogTitle></DialogHeader>
        <div>
          <p className="text-xs text-gray-400">Excel ünvanı</p>
          <p className="text-sm font-medium text-gray-800">{item.excel_name}</p>
        </div>
        {picked ? (
          <div className="border rounded p-3 text-sm flex items-center justify-between gap-2" data-testid="match-customer-preview">
            <span className="text-gray-700">{picked.name}</span>
            <button type="button" className="text-xs text-blue-600" onClick={() => setPicked(null)}>Değiştir</button>
          </div>
        ) : <CustomerSearch customers={customers} onPick={setPicked} />}
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="match-customer-create" onClick={() => setAsk({ type: 'create', item })} className="flex items-center gap-1">
            <UserPlus className="w-4 h-4" />Yeni Müşteri Olarak Ekle
          </Button>
          <Button data-testid="match-customer-confirm" disabled={!picked} onClick={() => setAsk({ type: 'match', item, customer: picked })}>Eşleştir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UnmatchedCustomers({ items, locks, customers, onLock }) {
  const [ask, setAsk] = useState(null);
  if (!items.length) return null;
  return (
    <Card><CardContent className="p-4" data-testid="import-unmatched-customers">
      <h3 className="font-semibold text-amber-700">Eşleşmeyen müşteri ünvanları ({items.length})</h3>
      <p className="text-xs text-gray-500 mb-2">Her ünvan için CRM'deki müşteriyi seçip "Eşleştir" deyin veya CRM'e yeni müşteri olarak ekleyin. Onaylanan satır kilitlenir.</p>
      <div className="divide-y max-h-[520px] overflow-y-auto">
        {items.map(item => (
          <UnmatchedRow key={item.excel_name} item={item} lock={locks[item.excel_name]} customers={customers} onAsk={setAsk} />
        ))}
      </div>
      <ConfirmDialog action={ask} onClose={() => setAsk(null)} onDone={onLock} />
    </CardContent></Card>
  );
}
